<template>
  <div class="mega-menu">
    <div class="mega-menu-categories">
      <router-link
        :to="linkTo(category.path)"
        class="mega-menu-alllink"
        @mouseenter.native="setActive(null)"
        @click.native="$emit('navigate')"
      >
        All {{ category.name }}
      </router-link>

      <MegaMenuCategoryColumn
        v-for="child in category.children"
        :key="child.id"
        :node="child"
        :active-id="activeNodeId"
        @hover="setActive"
        @navigate="$emit('navigate')"
      />
    </div>

    <!-- Only appears when the hovered category actually has brands filed
         under it — nothing to show for a plain "All Men"-style category.
         No panel chrome: it reads as an extension of the row you're on,
         not a separate boxed module. -->
    <div class="mega-menu-brands" v-if="activeNode && activeBrands.length">
      <router-link
        v-for="brand in activeBrands"
        :key="brand.id"
        :to="linkTo(activeNode.path + brand.slug + '/')"
        class="mega-menu-brand-link"
        @click.native="$emit('navigate')"
      >
        {{ brand.name }}
      </router-link>
    </div>
  </div>
</template>

<script>
import MegaMenuCategoryColumn from './MegaMenuCategoryColumn.vue';

// Flattens the category subtree so hovering any descendant (any depth) can
// resolve straight to its node without the column knowing the tree shape.
function flattenById(node, out) {
  out.set(node.id, node);
  for (const child of node.children || []) flattenById(child, out);
  return out;
}

export default {
  name: 'CategoryMegaMenu',
  components: { MegaMenuCategoryColumn },
  props: {
    category: { type: Object, required: true }
  },
  data() {
    return {
      // No node hovered yet, so no brands panel until the shopper hovers one.
      activeNodeId: null
    };
  },
  computed: {
    nodesById() {
      return flattenById(this.category, new Map());
    },
    activeNode() {
      return this.activeNodeId == null ? null : this.nodesById.get(this.activeNodeId);
    },
    activeBrands() {
      return (this.activeNode && this.activeNode.brands) || [];
    }
  },
  watch: {
    // Dropdown re-opens fresh each time rather than remembering the last hover.
    'category.id'() {
      this.activeNodeId = null;
    }
  },
  methods: {
    setActive(id) {
      this.activeNodeId = id;
    },
    linkTo(path) {
      return `/c/${String(path || '').split('/').filter(Boolean).join('/')}`;
    }
  }
};
</script>

<style scoped>
.mega-menu {
  display: flex;
  align-items: stretch;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.mega-menu-categories {
  display: flex;
  flex-direction: column;
  padding: 10px 0;
  min-width: 200px;
}

.mega-menu-alllink {
  padding: 8px 20px;
  font-weight: 600;
  color: #1b4b8f;
  text-decoration: none;
  font-size: 0.9rem;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 4px;
  padding-bottom: 12px;
}

.mega-menu-alllink:hover {
  text-decoration: underline;
}

.mega-menu-brands {
  padding: 12px 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 160px;
  border-left: 1px solid #f0f0f0;
}

.mega-menu-brand-link {
  display: block;
  padding: 6px 0;
  color: #666;
  text-decoration: none;
  font-size: 0.85rem;
  white-space: nowrap;
}

.mega-menu-brand-link:hover {
  color: #1b4b8f;
}
</style>
